/**
 * Metadatos de las vistas de Configuración: alimentan el hub `/settings` (tarjetas con
 * ícono, título, descripción y un modal de ayuda), la cabecera de cada vista y el menú
 * lateral de Configuración. `permission` = el permiso `*.view` que la hace visible.
 * `hub` = aparece como tarjeta en el hub / ítem del menú lateral.
 */
export interface SettingsViewHelp {
  intro: string;      // ¿Para qué sirve?
  steps: string[];    // Cómo se usa (paso a paso)
  tips?: string[];    // Consejos (opcional)
}

export interface SettingsViewMeta {
  key: string;          // coincide con el tab/segmento de ruta
  route: string;        // ruta destino
  icon: string;         // ícono lucide
  title: string;
  description: string;
  examples: string[];   // ejemplos cortos (resumen en la tarjeta)
  help: SettingsViewHelp; // guía detallada (modal)
  permission: string;   // permiso que la habilita
  hub: boolean;         // true = se muestra como tarjeta en el hub / menú lateral
}

export const SETTINGS_VIEWS: SettingsViewMeta[] = [
  {
    key: 'users', route: '/settings/users', icon: 'user-cog', title: 'Usuarios',
    description: 'Agentes del sistema: crea, edita, activa o elimina cuentas y asígnales un rol.',
    examples: [
      'Crear un agente nuevo y asignarle el rol “Agente”.',
      'Desactivar el acceso de alguien sin borrar su cuenta.',
    ],
    help: {
      intro: 'Administra las cuentas de los agentes que entran al CRM: quién existe, con qué correo, si está activo y qué rol (permisos) tiene.',
      steps: [
        'Toca “Nuevo Agente” y completa nombre, correo y contraseña.',
        'Elige el Rol: define exactamente qué podrá ver y hacer (los roles se crean en Configuración › Roles).',
        'Guarda. El agente ya puede iniciar sesión con ese correo.',
        'Para bloquear a alguien sin borrarlo, usa el interruptor “Activo”.',
      ],
      tips: [
        'Un agente inactivo no puede iniciar sesión, pero se conserva su historial.',
        'Solo un Super Administrador puede otorgar el rol Super Administrador.',
      ],
    },
    permission: 'users.view', hub: true,
  },
  {
    key: 'roles', route: '/settings/roles', icon: 'shield-check', title: 'Roles',
    description: 'Define qué puede ver y hacer cada perfil marcando permisos por módulo.',
    examples: [
      'Crear un rol “Cobranzas” con acceso solo a Recordatorios y FAQ.',
      'Al marcar “Probar campaña” se activan solos “Ver” y “Ver detalle”.',
    ],
    help: {
      intro: 'Un rol es un conjunto de permisos. En vez de dar acceso “de todo o nada”, marcas exactamente qué módulos y acciones puede usar cada perfil.',
      steps: [
        'Toca “Nuevo rol” y ponle un nombre (ej. “Supervisor de Cobranzas”).',
        'Abre cada módulo y marca las acciones que tendrá el rol.',
        'Al marcar una acción, sus requisitos se marcan solos (ej. “Probar campaña” activa “Ver” y “Ver detalle”).',
        'Guarda. Luego asigna ese rol a los usuarios en Configuración › Usuarios.',
      ],
      tips: [
        'Puedes partir de un preset (Agente, Supervisor…) y ajustarlo a tu gusto.',
        'No se puede borrar un rol que tenga usuarios asignados: reasígnalos primero.',
      ],
    },
    permission: 'roles.view', hub: true,
  },
  {
    key: 'teams', route: '/settings/teams', icon: 'users-round', title: 'Equipos',
    description: 'Agrupa agentes por equipo y asígnales una línea de WhatsApp.',
    examples: [
      'Crear el equipo “Ventas SCZ” y agregar sus integrantes.',
      'Asignar la línea “Santa Ines” a un equipo.',
    ],
    help: {
      intro: 'Los equipos agrupan agentes y se asocian a una línea de WhatsApp. El reparto de chats (round robin) y las coberturas trabajan por equipo.',
      steps: [
        'Toca “Nuevo Equipo” y ponle un nombre.',
        'Asigna la línea de WhatsApp (proyecto) del equipo.',
        'Marca los agentes que lo integran.',
        'Guarda.',
      ],
      tips: [
        'Un agente puede pertenecer a varios equipos.',
        'El equipo define a qué línea tienen acceso sus agentes.',
      ],
    },
    permission: 'teams.view', hub: true,
  },
  {
    key: 'availability', route: '/settings/availability', icon: 'coffee', title: 'Disponibilidad',
    description: 'Configura descansos, almuerzo y salida por equipo.',
    examples: [
      'Permitir 3 descansos de 5 minutos por día.',
      'Definir la ventana de almuerzo de 12:15 a 13:15.',
    ],
    help: {
      intro: 'Define las reglas de descanso, almuerzo y salida para cada equipo, y controla cuántas pausas puede tomar un agente por día.',
      steps: [
        'Elige el equipo a configurar.',
        'Ajusta la duración y la cantidad de descansos por día.',
        'Activa el almuerzo y su ventana horaria (ej. 12:15–13:15).',
        'Define la hora de salida (y si es automática). Guarda.',
      ],
      tips: [
        'Cada equipo puede tener su propia zona horaria.',
        'El agente ve estas reglas en el widget de disponibilidad del panel.',
      ],
    },
    permission: 'availability.config_view', hub: true,
  },
  {
    key: 'support', route: '/settings/support', icon: 'users', title: 'Soporte',
    description: 'Cobertura temporal: un equipo reemplaza a otro o suma apoyo por fechas.',
    examples: [
      'Cubrir a 3 agentes de vacaciones con el equipo TMK.',
      'Sumar apoyo extra a un equipo sin reemplazar a nadie.',
    ],
    help: {
      intro: 'Cobertura temporal entre equipos: cuando alguien sale de vacaciones otro equipo lo reemplaza, o simplemente se suma como apoyo extra al reparto.',
      steps: [
        'Elige el equipo de soporte y el equipo objetivo.',
        'Selecciona los usuarios a reemplazar (o déjalo vacío para apoyo extra).',
        'Define el rango de fechas (desde / hasta).',
        'Crea la asignación.',
      ],
      tips: [
        'Con usuarios seleccionados = reemplazo; sin usuarios = apoyo extra.',
        'Al terminar la cobertura, los chats vuelven a su dueño.',
      ],
    },
    permission: 'support.view', hub: true,
  },
  {
    key: 'cobradores', route: '/settings/cobradores', icon: 'dollar-sign', title: 'Cobradores',
    description: 'Cobradores por proyecto y su teléfono para el handoff del bot de cobranzas.',
    examples: [
      'Asignar un cobrador titular al proyecto “Santa Ines”.',
      'Editar el teléfono que se comparte con el cliente al derivar.',
    ],
    help: {
      intro: 'Define qué cobrador atiende cada proyecto y su teléfono. Cuando el bot de cobranzas deriva a un humano, usa estos datos para pasar el contacto correcto.',
      steps: [
        'Agrega proyectos (código → nombre legible, ej. SI → Santa Ines).',
        'Asigna un cobrador a un proyecto y márcalo como Titular o Apoyo.',
        'Escribe el teléfono que se compartirá con el cliente al derivar.',
        'Activa o desactiva cobradores según necesites.',
      ],
      tips: [
        'Si hay un solo cobrador en el proyecto, la derivación va directa.',
        'El titular tiene prioridad sobre el apoyo.',
      ],
    },
    permission: 'cobradores.view', hub: true,
  },
  {
    key: 'recordatorios', route: '/settings/recordatorios', icon: 'calendar-clock', title: 'Recordatorios',
    description: 'Plantilla y días de anticipación para avisar las cuotas por vencer.',
    examples: [
      'Activar el recordatorio 3 días antes del vencimiento.',
      'Importar las cuotas por vencer y enviar una de prueba.',
    ],
    help: {
      intro: 'Configura el aviso automático de cuotas por vencer por línea: qué plantilla de WhatsApp usar y con cuántos días de anticipación enviarla.',
      steps: [
        'Elige la línea y activa el recordatorio.',
        'Define la plantilla (aprobada en Meta), el idioma y los días de anticipación.',
        'Importa las cuotas por vencer (pegando el JSON del ERP).',
        'Puedes enviar una de prueba o ejecutar el cron ahora.',
      ],
      tips: [
        'Sin la plantilla aprobada en Meta, los envíos fallan.',
        'El cliente que responde lo atiende el bot IA automáticamente.',
      ],
    },
    permission: 'recordatorios.view', hub: true,
  },
  {
    key: 'campanas', route: '/settings/campanas', icon: 'send', title: 'Campañas de Recordatorio',
    description: 'Campañas push disparadas por el ERP, con su historial y reenvíos.',
    examples: [
      'Crear la campaña “recordatorio_1_dia” con su plantilla.',
      'Reenviar todos los envíos fallidos de una campaña.',
    ],
    help: {
      intro: 'Campañas de recordatorio disparadas por el ERP (push): cada campaña es una plantilla con su orden de variables, su URL de disparo y su historial de envíos.',
      steps: [
        'Crea la campaña (o carga un preset) con su plantilla y línea.',
        'Ordena las variables que llenan la plantilla.',
        'Copia la URL de disparo y compártela con el ERP.',
        'En “Historial” ves qué se envió y qué falló, con opción de reenvío.',
      ],
      tips: [
        'El 202 significa “aceptado”, no “entregado”: los fallos quedan en el Historial.',
        'Puedes reenviar un solo fallido o todos de golpe.',
      ],
    },
    permission: 'rec_campanas.view', hub: true,
  },
  {
    key: 'faqs', route: '/settings/faqs', icon: 'message-circle', title: 'FAQ',
    description: 'Respuestas automáticas por línea antes de gastar en la IA.',
    examples: [
      'Cargar “Tipo de cambio” con sus palabras clave.',
      'Desactivar una FAQ sin borrarla.',
    ],
    help: {
      intro: 'Respuestas automáticas por línea que el bot da antes de gastar en la IA. Si el mensaje coincide con una FAQ (por palabras clave o por la IA), responde tu texto exacto.',
      steps: [
        'Elige la línea de WhatsApp.',
        'Agrega la pregunta, la respuesta y las palabras clave (separadas por coma).',
        'Activa o desactiva cada FAQ según necesites.',
        'El bot la usará automáticamente en esa línea.',
      ],
      tips: [
        'Las palabras clave dan respuesta instantánea, sin gastar IA.',
        'Cada línea (cobranzas, comercial…) tiene sus propias FAQs.',
      ],
    },
    permission: 'faqs.view', hub: true,
  },
  {
    key: 'anuncios', route: '/settings/anuncios', icon: 'megaphone', title: 'Anuncios',
    description: 'Mapea anuncios de clic-a-WhatsApp a conjuntos y mira quién ingresó.',
    examples: [
      'Registrar un anuncio y rutearlo al conjunto “Montero”.',
      'Ver los chats que ingresaron por un anuncio.',
    ],
    help: {
      intro: 'Mapea los anuncios de clic-a-WhatsApp (Facebook/Instagram) a un “conjunto” para rutear el chat al equipo correcto, y mira quién ingresó por cada anuncio.',
      steps: [
        'Registra el identificador del anuncio y elige/crea su conjunto (ej. Montero).',
        'Habilita o deshabilita el anuncio (deshabilitado deja de rutear).',
        'Abre “Ingresos” para ver los chats que entraron por ese anuncio.',
        'Edita el conjunto o elimínalo cuando ya no aplique.',
      ],
      tips: [
        'Un anuncio deshabilitado conserva su registro pero no rutea.',
        'Elige el conjunto de la lista para no crear duplicados por una letra.',
      ],
    },
    permission: 'anuncios.view', hub: true,
  },
  {
    key: 'followups', route: '/settings/followups', icon: 'check-square', title: 'Campañas de Seguimiento',
    description: 'Envíos automáticos según las visitas/llamadas programadas en Zoho.',
    examples: [
      'Vincular el propósito “Visita programada” a una plantilla.',
      'Correr la revisión ahora para agendar las visitas próximas.',
    ],
    help: {
      intro: 'Envíos automáticos según las visitas o llamadas que los asesores programan en Zoho: recordatorios 24 h y 1 h antes, o una campaña de seguimiento.',
      steps: [
        'Crea un vínculo: elige la línea y el propósito de llamada exacto de Zoho.',
        'Si es visita, elige las plantillas de 24 h y 1 h antes; si no, la campaña a enviar.',
        'Usa “Correr revisión ahora” para agendar las visitas próximas.',
        'Prueba el envío con un número antes de dejarlo activo.',
      ],
      tips: [
        'El escaneo trae todas las visitas futuras programadas en Zoho.',
        'El propósito debe coincidir exactamente con el de Zoho.',
      ],
    },
    permission: 'followups.view', hub: true,
  },
  {
    key: 'campaigns', route: '/settings/campaigns', icon: 'megaphone', title: 'Campañas Masivas',
    description: 'Envíos masivos de plantillas a una lista de destinatarios.',
    examples: [
      'Crear una campaña, probarla y luego iniciarla.',
      'Agregar destinatarios a mano o pegando desde Excel.',
    ],
    help: {
      intro: 'Envíos masivos de una plantilla de WhatsApp a una lista de destinatarios (desde Zoho o cargados a mano), con prueba previa y seguimiento de entregados/fallidos.',
      steps: [
        'Crea la campaña con su línea, plantilla e idioma.',
        'Agrega destinatarios (a mano, pegando de Excel o importando de Zoho).',
        'Completa las variables y envía una prueba a tus números.',
        'Cuando esté OK, toca “Iniciar” para el envío masivo.',
      ],
      tips: [
        'Solo puedes editar o eliminar mientras la campaña está en borrador.',
        'Puedes reintentar solo los destinatarios fallidos.',
      ],
    },
    permission: 'campaigns.view', hub: true,
  },
  {
    key: 'phone-accounts', route: '/settings/phone-accounts', icon: 'smartphone', title: 'Números de WhatsApp',
    description: 'Gestiona las líneas: token, color, restricciones, bot IA y creación de leads.',
    examples: [
      'Agregar una línea nueva con su Phone ID y token.',
      'Activar el bot IA en una línea de cobranzas.',
    ],
    help: {
      intro: 'Gestiona las líneas de WhatsApp (números): su token de Meta, color, estado, si crea leads en Zoho y si el bot IA responde.',
      steps: [
        'Agrega una línea con su Phone ID, número y Access Token de Meta.',
        'Elige un color para identificarla y su estado (activa/inactiva).',
        'Activa “Crear leads” y/o “Bot IA” según la línea.',
        'Configura el mapeo de campos del Lead si esa línea crea leads.',
      ],
      tips: [
        'Una línea inactiva se oculta del panel pero sigue gestionable aquí.',
        'El bot IA solo responde en las líneas donde lo actives.',
      ],
    },
    permission: 'phone_accounts.view', hub: true,
  },
  // Herramienta (no aparece en el hub; se llega desde el sidebar “Herramientas”).
  {
    key: 'quick-replies', route: '/quick-replies', icon: 'zap', title: 'Respuestas Rápidas',
    description: 'Crea atajos personales o para tu equipo.',
    examples: [
      'Guardar un mensaje de bienvenida como “/hola”.',
      'Compartir una respuesta con todo el equipo.',
    ],
    help: {
      intro: 'Atajos de mensajes que insertas en el chat escribiendo “/”. Pueden ser personales, de equipo, de proyecto o globales.',
      steps: [
        'Toca “Nueva Respuesta” y define el atajo (ej. /hola).',
        'Escribe el mensaje y, si quieres, adjunta una imagen o archivo.',
        'Elige quién la usará (privada, equipo, proyecto o global).',
        'En el chat, escribe “/” y elígela para insertarla.',
      ],
      tips: [
        'Las respuestas globales o de proyecto las crean los administradores.',
        'Sirven para saludos, datos de pago, ubicaciones, etc.',
      ],
    },
    permission: 'quick_replies.view', hub: false,
  },
];

/** Permisos de las vistas que aparecen en el hub (para gatear el link “Configuración”). */
export const CONFIG_HUB_PERMISSIONS: string[] = SETTINGS_VIEWS.filter((v) => v.hub).map((v) => v.permission);

/** Vistas que se muestran en el hub / menú lateral de Configuración. */
export const CONFIG_HUB_VIEWS: SettingsViewMeta[] = SETTINGS_VIEWS.filter((v) => v.hub);
